import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../../../../store/storeAuth';

import {
  Box,
  Button,
  Collapse,
  Typography,
} from '@mui/material';
import {
  CaretDown,
  CaretUp,
  HardDrives,
  Plus,
} from '@phosphor-icons/react';
import { DeleteNamespaceDialog } from '../../../UI/DeleteNamespaceDialog';
import { VectorMemoryModal } from '../../../UI/VectorMemoryModal';
import { VectorItem } from './VectorItem';

import IconButtonTooltip from '../../../UI/IconButtonTooltip/IconButtonTooltip';

import { vectorQueries } from '../../../../queries/vector';
import { deleteNamespace, DeleteNamespaceBody } from '../../../../api/deleteNamespace';

import type { VectorApiParams } from '../../../../api/createNamespace';
import type { VectorEmbeddingModel } from '../../../../types/vector';
import { spacesQueries } from '../../../../queries/spaces';

interface VectorProps {
  handleCloseSideBarMobile: () => void;
  spaceId?: string;
  // space?: any;
  modifySpaceVector?: (space: any) => void;
};

const Vector = ({
  handleCloseSideBarMobile,
  spaceId,
  // space,
}: VectorProps) => {
  const { data: userNamespaces } = useQuery({ 
    ...vectorQueries.allNamespaces(),
    enabled: !spaceId
  })
  const { data: spaces } = useQuery({
    ...spacesQueries.list(spaceId!),
    enabled: !!spaceId
  })

  const { user: authUser, sidebarOpen } = useAuthStore((state) => state)

  const [vectorCollapsed, setVectorCollapsed] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false)
  const [openVectorUploadModal, setOpenVectorUploadModal] =
    useState<{ open: boolean; namespaceId?: string }>({ open: false, });
  const [selectedNamespace, setSelectedNamespace] = useState<{
    embeddingModelName: VectorEmbeddingModel
    id: string
    name: string
  } | undefined>(undefined)

  const queryClient = useQueryClient()

  const handleAlertOpen = (open: boolean) => {
    setAlertOpen(open)
  }

  const handleOpenVectorModal = ({
    namespaceId, open
  }: {
    namespaceId?: string
    open: boolean
  }) => {
    setOpenVectorUploadModal(() => ({
      open,
      namespaceId
    }))
  }

  const handleSelectedNamespaceChange = ({
    embeddingModelName, id, name
  }: {
    embeddingModelName: VectorEmbeddingModel
    id: string
    name: string
  }) => {
    setSelectedNamespace({ embeddingModelName, id, name })
  }

  const handleNamespaceDelete = ({
    embeddingModelName,
    namespaceId,
    namespaceName,
    spaceId,
  }: {
    embeddingModelName: VectorEmbeddingModel
    namespaceId: string
    namespaceName: string
    spaceId?: string
  }) => {
    deleteNamespaceFn({
      body: {
        embeddingModelName
      },
      params: {
        nameSpace: namespaceName,
        id: namespaceId,
        spaceId: spaceId
      }
    })
  }

  const {
    mutate: deleteNamespaceFn,
    variables,
    status,
    reset
  } = useMutation({
    mutationFn: ({
      body, params
    }: {
      body: DeleteNamespaceBody
      params: VectorApiParams
    }) => deleteNamespace({
      body,
      params
    }),
    onSuccess: () => {
      return queryClient.invalidateQueries({ queryKey: ['namespaces'], refetchType: 'all' })
    },
    onError: async () => {
      setTimeout(() => {
        reset()
      }, 2000)
    }
  })

  const namespaces = spaceId 
    ? spaces?.data.namespaces
    : userNamespaces?.data

  return (
    <>
      <Box
        className={`layouts_sidebar_menu--btn align-items-center`}
        onClick={() => setVectorCollapsed(!vectorCollapsed)}
        sx={{
          pt: '4px',
          display: 'flex',
          justifyContent: !sidebarOpen ? 'center' : 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
          paddingLeft: '10px',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
        }}
      >
        <Box
          className="menu menu--on-hover"
          sx={{
            display: 'flex',
            width: '100%',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingRight: '7px',
            paddingLeft: '8px',
            paddingBlock: '0.25rem',

            '&:hover': {
              backgroundColor:
                authUser?.theme === 'dark' ? '#101828' : '#E7F0FF',
              borderRadius: '4px',
            },
          }}
        >
          <Box sx={{ display: 'flex', columnGap: '8px' }}>
            <HardDrives
              className="active-menu"
              color={authUser?.theme === 'dark' ? '#D0D5DD' : '#667085'}
              size={20}
              weight="regular"
              style={{ alignSelf: 'center' }}
            />

            {sidebarOpen && (
              <Typography
                className="text-capitalize active-menu"
                sx={{
                  color: authUser?.theme === 'dark' ? '#D0D5DD' : '#475467',
                  fontSize: '16px',
                  fontWeight: 400,
                  lineHeight: '24px',
                  '&:hover': {
                    color: '#3650DE',
                  },
                }}
              >
                Vector Memory
              </Typography>
            )}
          </Box>

          <Box sx={{ alignSelf: 'center', display: 'flex', gap: '8px' }}>
            <IconButtonTooltip
              title="Create new Vector"
              onClick={event => {
                event.stopPropagation();
                setOpenVectorUploadModal({
                  open: true,
                });
              }}
            >
              <Plus
                size={16}
                className="menu-icons active-menu"
                style={{
                  color: authUser?.theme === 'dark' ? '#D0D5DD' : '#667085',
                  cursor: 'pointer',
                }}
              />
            </IconButtonTooltip>

            {sidebarOpen && (
              <IconButtonTooltip title="Collapse">
                {!vectorCollapsed ? (
                  <CaretDown
                    className="active-menu"
                    size={16}
                    color={authUser?.theme === 'dark' ? '#D0D5DD' : '#667085'}
                  />
                ) : (
                  <CaretUp
                    className="active-menu"
                    size={16}
                    color={authUser?.theme === 'dark' ? '#D0D5DD' : '#667085'}
                  />
                )}
              </IconButtonTooltip>
            )}
          </Box>
        </Box>
      </Box>

      <Collapse
        in={vectorCollapsed}
        timeout={500}
        sx={{ overflowY: 'auto' }}
      >
        <Box
          style={{ paddingLeft: '27px' }}
          gap={'8px'}
        >
          {namespaces?.map(({ embeddingModelName, id, name }) => (
            <VectorItem
              key={id}
              deleteStatus={status}
              embeddingModelName={embeddingModelName}
              id={id}
              name={name}
              selectedNamespace={variables?.params.nameSpace}
              onAlertOpen={handleAlertOpen}
              onCloseSideBarMobile={handleCloseSideBarMobile}
              onOpenVectorModal={handleOpenVectorModal}
              onSelectedNamespaceChange={handleSelectedNamespaceChange}
            />
          ))}
        </Box>
      </Collapse>

      {selectedNamespace ? (
        <DeleteNamespaceDialog
          open={alertOpen}
          embeddingModelName={selectedNamespace.embeddingModelName}
          id={selectedNamespace.id}
          name={selectedNamespace.name}
          onOpenChange={handleAlertOpen}
          onNamespaceDelete={handleNamespaceDelete}
        />
      ) : null}

      <VectorMemoryModal 
        spaceId={spaceId}
        namespaceId={openVectorUploadModal.namespaceId}
        open={openVectorUploadModal.open}
        onOpenChange={handleOpenVectorModal}
      />

    </>
  );
};

export default Vector;
